import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import TextButton from '@design-system/text-button';
import { ETheme } from '@datatype/type';
import { drawSeshatBackground, geometryPoints, readSeshat, ISeshatImport } from '@util/gridgio-seshat';
import PdfBackground from '@component/gridgio/lab/pdf-background';
import * as color from '@constant/color';
import * as size from '@constant/size';
import * as font from '@constant/font';

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
`;
const ControlContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${size.MARGIN_1};
  font: ${font.FONT_SMALL_400};
  select,
  input[type='number'] {
    max-width: 100%;
    min-width: 0;
    height: ${size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT};
    box-sizing: border-box;
    font: ${font.FONT_SMALL_400};
    border: ${size.DESIGN_SYSTEM_BUTTON_BORDER_WIDTH} solid ${color.GRAY_2};
    border-radius: ${size.BORDER_RADIUS_1};
    padding: 0 ${size.DESIGN_SYSTEM_BUTTON_SMALL_PADDING_X};
    background: ${color.WHITE_1};
    color: ${color.BLACK_2};
  }
  label {
    display: inline-flex;
    align-items: center;
    gap: ${size.MARGIN_1};
    min-height: ${size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT};
    white-space: nowrap;
  }
  input[type='checkbox'] {
    accent-color: ${color.CYAN_2};
  }
`;
const PageControlContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${size.MARGIN_1};
  flex-wrap: wrap;
  padding: 0;
  border: 0;
  border-radius: ${size.BORDER_RADIUS_2};
  background: transparent;
`;
const DrawingSelect = styled.select`
  min-width: 120px !important;
  font: inherit;
  font-size: 14px;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${color.CYAN_2};
    outline-offset: 2px;
  }
`;
const PreviewContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: ${size.MARGIN_2};
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const ViewerContainer = styled.div`
  min-width: 0;
`;
const ZoomControlContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${size.MARGIN_1};
  margin-bottom: ${size.MARGIN_1};
`;
const DrawingScrollContainer = styled.div`
  overflow: auto;
  max-height: 75vh;
  overscroll-behavior: contain;
  border: 1px solid ${color.GRAY_2};
  background: ${color.WHITE_1};
`;
const DrawingContainer = styled.div<{ $zoom: number; $ratio: number }>`
  position: relative;
  width: ${(props) => props.$zoom * 100}%;
  aspect-ratio: ${(props) => props.$ratio};
  canvas,
  svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }
`;
const DetailContainer = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: ${size.MARGIN_1};
`;
const DamageItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-height: 300px;
  overflow: auto;
`;
const DamageItemContainer = styled.button<{ $selected: boolean }>`
  padding: ${size.MARGIN_1};
  border: ${size.DESIGN_SYSTEM_BUTTON_BORDER_WIDTH} solid ${(props) => (props.$selected ? color.CYAN_2 : color.GRAY_2)};
  background: ${color.WHITE_1};
  text-align: left;
  border-radius: ${size.DESIGN_SYSTEM_BUTTON_BORDER_RADIUS};
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${color.CYAN_2};
    outline-offset: 2px;
  }
`;
const TEXT_DESIGN = { box: { alignX: 'left' }, text: { size: 'small', color: color.BLACK_2 } } as const;

const Home: React.FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const drawingScrollRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ x: number; y: number; left: number; top: number; moved: boolean; pointerId: number } | null>(
    null,
  );
  const suppressClickRef = useRef(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const requestRef = useRef(0);
  const [data, setData] = useState<ISeshatImport | null>(null);
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [pdfIndex, setPdfIndex] = useState(0);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfMetadata, setPdfMetadata] = useState({ ratio: 1.414, totalPages: 1 });
  const [pdfReady, setPdfReady] = useState(false);
  const [background, setBackground] = useState<'dxf' | 'pdf'>('dxf');
  const [alignment, setAlignment] = useState({ x: 0, y: 0, scale: 100 });
  const [alignmentInput, setAlignmentInput] = useState({ x: '0', y: '0', scale: '100' });
  const [pageId, setPageId] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [showBackground, setShowBackground] = useState(true);
  const [showDamages, setShowDamages] = useState(true);
  const [showNumbers, setShowNumbers] = useState(true);
  const page = data?.document.pages.find((item) => item.id === pageId);
  const damages = data?.document.damages.filter((item) => item.page_id === pageId) ?? [];
  const selected = damages.find((item) => item.id === selectedId);

  useEffect(() => {
    const clearDrag = () => {
      dragRef.current = null;
    };
    window.addEventListener('pointerup', clearDrag);
    window.addEventListener('pointercancel', clearDrag);
    window.addEventListener('blur', clearDrag);
    return () => {
      requestRef.current += 1;
      window.removeEventListener('pointerup', clearDrag);
      window.removeEventListener('pointercancel', clearDrag);
      window.removeEventListener('blur', clearDrag);
    };
  }, []);
  useEffect(() => {
    if (canvasRef.current && data && pageId) drawSeshatBackground(canvasRef.current, data, pageId);
  }, [data, pageId, background]);

  const handlePdfMetadata = useCallback((metadata: { ratio: number; totalPages: number }) => {
    setPdfMetadata(metadata);
    setPdfReady(true);
  }, []);

  const resetAlignment = () => {
    setAlignment({ x: 0, y: 0, scale: 100 });
    setAlignmentInput({ x: '0', y: '0', scale: '100' });
  };

  const commitAlignment = (key: 'x' | 'y' | 'scale') => {
    const draft = alignmentInput[key].trim();
    const parsed = draft === '' ? (key === 'scale' ? 100 : 0) : Number(draft);
    const value = Number.isFinite(parsed)
      ? key === 'scale'
        ? Math.max(1, Math.min(500, parsed))
        : parsed
      : alignment[key];
    setAlignment((current) => ({ ...current, [key]: value }));
    setAlignmentInput((current) => ({ ...current, [key]: String(value) }));
  };

  const handleZoom = (next: number) => {
    const value = Math.max(1, Math.min(8, next));
    const viewport = drawingScrollRef.current;
    const centerX = viewport ? (viewport.scrollLeft + viewport.clientWidth / 2) / zoom : 0;
    const centerY = viewport ? (viewport.scrollTop + viewport.clientHeight / 2) / zoom : 0;
    setZoom(value);
    requestAnimationFrame(() => {
      if (!viewport) return;
      viewport.scrollLeft = value === 1 ? 0 : centerX * value - viewport.clientWidth / 2;
      viewport.scrollTop = value === 1 ? 0 : centerY * value - viewport.clientHeight / 2;
    });
  };

  const handlePdfPageChange = (value: number) => {
    const next = Number.isInteger(value) ? Math.max(1, Math.min(pdfMetadata.totalPages, value)) : pdfPage;
    setPdfPage(next);
  };

  const handleFile = async (file?: File) => {
    if (!file) return;
    const request = ++requestRef.current;
    setLoading(true);
    setError('');
    try {
      const result = await readSeshat(file);
      if (request !== requestRef.current) return;
      setData(result);
      setPageId(result.document.pages[0].id);
      setSelectedId('');
      setZoom(1);
    } catch (reason) {
      if (request === requestRef.current) setError(reason instanceof Error ? reason.message : '파일을 읽지 못했어요.');
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  };

  const PdfItems = pdfFiles.map((file, index) => (
    <option key={`${file.name}-${file.lastModified}`} value={index}>
      {file.name}
    </option>
  ));
  const PageItems = data?.document.pages.map((item) => (
    <option key={item.id} value={item.id}>
      도면 {item.index + 1}
    </option>
  ));
  const DamageItems = damages.map((damage) => (
    <DamageItemContainer
      type='button'
      key={damage.id}
      $selected={selectedId === damage.id}
      onClick={() => setSelectedId(damage.id)}
      aria-pressed={selectedId === damage.id}>
      <TextBlock
        text={`${damage.display_number} · ${damage.classifications.map((c) => c.code).join(', ')}`}
        design={TEXT_DESIGN}
      />
    </DamageItemContainer>
  ));
  const ShapeItems =
    page &&
    damages.map((damage) => {
      const shapes = damage.geometries.map((geometry) => {
        const points = geometryPoints(geometry)
          .map(([x, y]) => `${x * 1000},${(y * 1000 * page.height_pt) / page.width_pt}`)
          .join(' ');
        const closed = geometry.kind === 'polygon' || geometry.kind === 'ellipse';
        return (
          <polyline
            key={geometry.id}
            points={points + (closed && points ? ` ${points.split(' ')[0]}` : '')}
            fill={closed ? '#e6505020' : 'none'}
            vectorEffect='non-scaling-stroke'
          />
        );
      });
      const first = damage.geometries[0]?.points[0];
      return (
        <g
          key={damage.id}
          role='button'
          tabIndex={0}
          aria-label={`손상 ${damage.display_number}`}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setSelectedId(damage.id);
            }
          }}
          stroke={selectedId === damage.id ? '#008cba' : '#dc3939'}
          strokeWidth={selectedId === damage.id ? 3 : 1.5}
          onClick={() => setSelectedId(damage.id)}
          style={{ cursor: 'pointer' }}
          pointerEvents='visiblePainted'>
          <title>{`${damage.display_number} · ${damage.classifications.map((c) => c.code).join(', ')}`}</title>
          {shapes}
          {showNumbers && first && (
            <text
              x={first[0] * 1000}
              y={(first[1] * 1000 * page.height_pt) / page.width_pt - 4}
              stroke='none'
              fill='#c02929'
              fontSize='9'>
              {damage.display_number}
            </text>
          )}
        </g>
      );
    });
  const PropertyItems = selected?.classifications.flatMap((classification) =>
    classification.properties.map((property, index) => (
      <TextBlock
        key={`${selected.id}-${classification.code}-${index}`}
        text={`${property.key}: ${property.manual?.value ?? property.automatic?.value ?? '—'} ${property.unit}`}
        design={TEXT_DESIGN}
      />
    )),
  );

  return (
    <Wrapper>
      <TextBlock
        text='Seshat 도면 가져오기'
        design={{ box: { alignX: 'left' }, text: { size: 'large', bold: true, color: color.BLACK_5 } }}
      />
      <Gap height={size.MARGIN_1} />
      <TextBlock
        text='파일을 선택해 DXF 또는 PDF 배경과 추출된 손상의 위치를 비교하세요. 파일은 브라우저에서만 읽으며 서버에 저장하지 않아요.'
        design={TEXT_DESIGN}
      />
      <Gap height={size.MARGIN_2} />
      <input
        ref={inputRef}
        type='file'
        accept='.seshat'
        aria-label='Seshat 파일 선택'
        hidden
        onChange={(event) => {
          void handleFile(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      <input
        ref={pdfInputRef}
        type='file'
        accept='.pdf,application/pdf'
        multiple
        aria-label='비교할 PDF 파일 선택'
        hidden
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          if (files.length) {
            setPdfReady(false);
            setPdfMetadata({ ratio: 1.414, totalPages: 1 });
            setPdfFiles(files);
            setPdfIndex(0);
            setPdfPage(1);
            setBackground('pdf');
            resetAlignment();
          }
          event.target.value = '';
        }}
      />
      <ControlContainer>
        <TextButton
          height={size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT}
          text='파일 불러오기'
          width='130px'
          theme={ETheme.CYAN}
          size='small'
          shape='round'
          isLoading={loading}
          handleClick={() => inputRef.current?.click()}
        />
        <TextButton
          height={size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT}
          text='PDF 불러오기 (여러 파일)'
          width='200px'
          theme={ETheme.CYAN}
          kind='outlined'
          size='small'
          shape='round'
          handleClick={() => pdfInputRef.current?.click()}
        />
        {page && (
          <>
            <DrawingSelect
              aria-label='도면 선택'
              value={pageId}
              onChange={(event) => {
                setPageId(event.target.value);
                setSelectedId('');
                setZoom(1);
              }}>
              {PageItems}
            </DrawingSelect>
            <label>
              <input
                type='checkbox'
                checked={showBackground}
                onChange={(event) => setShowBackground(event.target.checked)}
              />{' '}
              배경
            </label>
            <label>
              <input type='checkbox' checked={showDamages} onChange={(event) => setShowDamages(event.target.checked)} />{' '}
              손상
            </label>
            <label>
              <input type='checkbox' checked={showNumbers} onChange={(event) => setShowNumbers(event.target.checked)} />{' '}
              번호
            </label>
          </>
        )}
      </ControlContainer>
      <Gap height={size.MARGIN_1} />
      {pdfFiles.length > 0 && (
        <ControlContainer>
          <label>
            <TextBlock text='배경' design={TEXT_DESIGN} />
            <select
              style={{ width: 110 }}
              value={background}
              onChange={(event) => setBackground(event.target.value as 'dxf' | 'pdf')}>
              <option value='dxf'>내부 DXF</option>
              <option value='pdf'>PDF</option>
            </select>
          </label>
          {background === 'pdf' && (
            <>
              <select
                aria-label='비교할 PDF'
                style={{ width: 210 }}
                value={pdfIndex}
                onChange={(event) => {
                  setPdfReady(false);
                  setPdfMetadata({ ratio: 1.414, totalPages: 1 });
                  setPdfIndex(Number(event.target.value));
                  setPdfPage(1);
                }}>
                {PdfItems}
              </select>
              <PageControlContainer role='group' aria-label='PDF 페이지 이동'>
                <TextButton
                  height={size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT}
                  text='이전'
                  icon='left'
                  arrangement='icon-text'
                  width='75px'
                  shape='round'
                  theme={ETheme.CYAN}
                  kind='outlined'
                  size='small'
                  disabled={!pdfReady || pdfPage <= 1}
                  handleClick={() => handlePdfPageChange(pdfPage - 1)}
                />
                <TextBlock
                  text={`${pdfPage} / ${pdfMetadata.totalPages} 페이지`}
                  design={{
                    box: { width: '90px', height: size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT },
                    text: { size: 'small', color: color.BLACK_2 },
                  }}
                />
                <TextButton
                  height={size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT}
                  text='다음'
                  icon='right'
                  arrangement='text-icon'
                  width='75px'
                  shape='round'
                  theme={ETheme.CYAN}
                  kind='outlined'
                  size='small'
                  disabled={!pdfReady || pdfPage >= pdfMetadata.totalPages}
                  handleClick={() => handlePdfPageChange(pdfPage + 1)}
                />
              </PageControlContainer>
            </>
          )}
        </ControlContainer>
      )}
      {pdfFiles.length > 0 && background === 'pdf' && (
        <>
          <Gap height={size.MARGIN_1} />
          <ControlContainer role='group' aria-label='손상 레이어 위치 보정'>
            <label>
              <TextBlock text='손상 X (%)' design={TEXT_DESIGN} />
              <input
                type='number'
                step='0.1'
                value={alignmentInput.x}
                style={{ width: 75 }}
                onChange={(event) => setAlignmentInput((current) => ({ ...current, x: event.target.value }))}
                onBlur={() => commitAlignment('x')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitAlignment('x');
                  }
                  if (event.key === 'Escape') setAlignmentInput((current) => ({ ...current, x: String(alignment.x) }));
                }}
              />
            </label>
            <label>
              <TextBlock text='손상 Y (%)' design={TEXT_DESIGN} />
              <input
                type='number'
                step='0.1'
                value={alignmentInput.y}
                style={{ width: 75 }}
                onChange={(event) => setAlignmentInput((current) => ({ ...current, y: event.target.value }))}
                onBlur={() => commitAlignment('y')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitAlignment('y');
                  }
                  if (event.key === 'Escape') setAlignmentInput((current) => ({ ...current, y: String(alignment.y) }));
                }}
              />
            </label>
            <label>
              <TextBlock text='손상 크기 (%)' design={TEXT_DESIGN} />
              <input
                type='number'
                min={1}
                max={500}
                step='0.1'
                value={alignmentInput.scale}
                style={{ width: 75 }}
                onChange={(event) => setAlignmentInput((current) => ({ ...current, scale: event.target.value }))}
                onBlur={() => commitAlignment('scale')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitAlignment('scale');
                  }
                  if (event.key === 'Escape')
                    setAlignmentInput((current) => ({ ...current, scale: String(alignment.scale) }));
                }}
              />
            </label>
            <TextButton
              height={size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT}
              text='정렬 초기화'
              width='95px'
              shape='round'
              theme={ETheme.GRAY}
              kind='outlined'
              size='small'
              handleClick={resetAlignment}
            />
          </ControlContainer>
        </>
      )}
      <Gap height={size.MARGIN_1} />
      {error && (
        <div role='alert'>
          <TextBlock text={error} design={{ text: { size: 'small', color: color.RED_3 } }} />
        </div>
      )}
      {data && page && (
        <>
          <TextBlock
            text={`${data.document.title} · ${data.document.pages.length}페이지 · 전체 손상 ${data.document.damages.length}건 · 현재 도면 ${damages.length}건`}
            design={TEXT_DESIGN}
          />
          <Gap height={size.MARGIN_1} />
          {background === 'pdf' && (
            <TextBlock
              text='Seshat 도면과 PDF 페이지를 각각 선택하세요. 최초에는 손상 좌표를 PDF 전체 페이지에 대응시켜요. 출력 여백이 다르면 X·Y·크기를 조정해 비교하세요. 정렬 값은 현재 비교에만 적용돼요.'
              design={TEXT_DESIGN}
            />
          )}
          {background === 'dxf' && (
            <TextBlock
              text='실험용 간이 DXF 표시: 블록·해치·스플라인, 폴리라인 곡선 및 CAD 전용 글꼴은 재현하지 않아요. 원본 DXF에 포함된 손상도 배경에 남아 있을 수 있어요.'
              design={TEXT_DESIGN}
            />
          )}
          <Gap height={size.MARGIN_2} />
          <PreviewContainer>
            <ViewerContainer>
              <ZoomControlContainer>
                <TextButton
                  height={size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT}
                  text='축소'
                  icon='zoom-out'
                  arrangement='icon-text'
                  width='75px'
                  theme={ETheme.CYAN}
                  kind='outlined'
                  size='small'
                  shape='round'
                  disabled={zoom <= 1}
                  handleClick={() => handleZoom(zoom - 0.5)}
                />
                <TextBlock text={`${Math.round(zoom * 100)}%`} design={TEXT_DESIGN} />
                <TextButton
                  height={size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT}
                  text='확대'
                  icon='zoom-in'
                  arrangement='icon-text'
                  width='75px'
                  theme={ETheme.CYAN}
                  kind='outlined'
                  size='small'
                  shape='round'
                  disabled={zoom >= 8}
                  handleClick={() => handleZoom(zoom + 0.5)}
                />
                <TextButton
                  height={size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT}
                  text='화면 맞춤'
                  width='95px'
                  theme={ETheme.GRAY}
                  kind='outlined'
                  size='small'
                  shape='round'
                  handleClick={() => handleZoom(1)}
                />
                <TextBlock text='확대 후 도면을 드래그해 이동하세요.' design={TEXT_DESIGN} />
              </ZoomControlContainer>
              <DrawingScrollContainer
                ref={drawingScrollRef}
                style={{
                  userSelect: zoom > 1 ? 'none' : 'auto',
                  cursor: zoom > 1 ? 'grab' : 'auto',
                  touchAction: zoom > 1 ? 'none' : 'auto',
                }}
                onPointerDown={(event) => {
                  if (zoom <= 1 || event.button !== 0 || !event.isPrimary) return;
                  suppressClickRef.current = false;
                  dragRef.current = {
                    x: event.clientX,
                    y: event.clientY,
                    left: event.currentTarget.scrollLeft,
                    top: event.currentTarget.scrollTop,
                    moved: false,
                    pointerId: event.pointerId,
                  };
                }}
                onPointerMove={(event) => {
                  const drag = dragRef.current;
                  if (!drag || drag.pointerId !== event.pointerId) return;
                  const dx = event.clientX - drag.x,
                    dy = event.clientY - drag.y;
                  if (!drag.moved && Math.hypot(dx, dy) < 4) return;
                  if (!drag.moved) event.currentTarget.setPointerCapture(event.pointerId);
                  drag.moved = true;
                  suppressClickRef.current = true;
                  event.currentTarget.scrollLeft = drag.left - dx;
                  event.currentTarget.scrollTop = drag.top - dy;
                }}
                onPointerUp={() => {
                  dragRef.current = null;
                }}
                onPointerCancel={() => {
                  dragRef.current = null;
                  suppressClickRef.current = false;
                }}
                onLostPointerCapture={() => {
                  dragRef.current = null;
                }}
                onClickCapture={(event) => {
                  if (suppressClickRef.current) {
                    event.stopPropagation();
                    suppressClickRef.current = false;
                  }
                }}>
                <DrawingContainer
                  $zoom={zoom}
                  $ratio={background === 'pdf' ? pdfMetadata.ratio : page.width_pt / page.height_pt}>
                  <div style={{ visibility: showBackground ? 'visible' : 'hidden' }}>
                    {background === 'pdf' && pdfFiles[pdfIndex] ? (
                      <PdfBackground
                        key={`${pdfIndex}-${pdfFiles[pdfIndex].lastModified}`}
                        file={pdfFiles[pdfIndex]}
                        page={pdfPage}
                        handleMetadata={handlePdfMetadata}
                      />
                    ) : (
                      <canvas ref={canvasRef} />
                    )}
                  </div>
                  <svg
                    viewBox={`0 0 1000 ${(1000 * page.height_pt) / page.width_pt}`}
                    preserveAspectRatio='none'
                    aria-label='추출된 손상 도면'>
                    <g
                      transform={
                        background === 'pdf'
                          ? `translate(${alignment.x * 10} ${(alignment.y * 10 * page.height_pt) / page.width_pt}) scale(${alignment.scale / 100})`
                          : undefined
                      }>
                      {showDamages && ShapeItems}
                    </g>
                  </svg>
                </DrawingContainer>
              </DrawingScrollContainer>
            </ViewerContainer>
            <DetailContainer>
              <TextBlock text='손상 목록' design={{ text: { size: 'medium', bold: true } }} />
              <DamageItemsContainer>{DamageItems}</DamageItemsContainer>
              {!damages.length && <TextBlock text='이 도면에는 추출된 손상이 없어요.' design={TEXT_DESIGN} />}
              <Gap height={size.MARGIN_1} />
              <TextBlock
                text={
                  selected
                    ? `${selected.display_number} · ${selected.classifications.map((c) => c.code).join(', ')}`
                    : '도면이나 목록에서 손상을 선택하세요.'
                }
                design={TEXT_DESIGN}
              />
              {PropertyItems}
              {selected && <TextBlock text={selected.notes.join('\n') || '메모 없음'} design={TEXT_DESIGN} />}
            </DetailContainer>
          </PreviewContainer>
        </>
      )}
    </Wrapper>
  );
};

export default Home;
